import Link from "next/link";
import { Trans, useTranslation } from "next-i18next";

import ContactForm from "@/components/Contact/ContactForm";
import ContactInfo from "@/components/Contact/ContactInfo";
import styles from "@/components/Contact/styles.module.css";
import { Button } from "@/components/elements/Button";
import { Text } from "@/components/elements/Text";
import { PageLayout } from "@/components/layouts";
import { NotificationBanner } from "@/components/NotificationBanner";
import { PaddingWrapper } from "@/components/PaddingWrapper";
import { SocialLinks } from "@/layouts/components/Footer/components/SocialLinks";
import { getSiteSettings } from "@/services/sanity";
import { getHelpEmail, isNewCustomerServiceActive } from "@/utils/helpers";
import { getAllTranslationsServerSide } from "@/utils/i18n";

export default function About({
  siteSettings,
  helpEmail,
  showNewCustomerServiceDetails,
}) {
  const { t } = useTranslation("contact");
  const { faq_page_visible } = siteSettings;

  return (
    <PageLayout
      title={t("contact_meta_title")}
      description={t("contact_meta_description")}
      headerTheme="dark"
    >
      <div className={styles.container}>
        <PaddingWrapper disable className={styles.contactUsTextContainer}>
          <Text as="h1" size={"l"} responsive className={styles.title}>
            {t("contact_title")}
          </Text>

          <Trans
            ns="contact"
            i18nKey={
              showNewCustomerServiceDetails
                ? "contact_description_new"
                : "contact_description"
            }
            components={{
              helpLink: (
                <Link href="https://www.orkla.com/consumer-service/customer-service-no/" />
              ),
              helpEmail: <Link href={`mailto:${helpEmail}`} />,
            }}
            values={{ email: helpEmail }}
          />

          {(faq_page_visible ?? true) && (
            <Button theme="outline-square" pageLink="FAQ">
              {t("contact_page_view_all_FAQs_CTA_text")}
            </Button>
          )}
        </PaddingWrapper>
        <PaddingWrapper className={styles.wrapper} onlyY>
          <ContactForm />
          <ContactInfo />
        </PaddingWrapper>

        <PaddingWrapper onlyY className={styles.followUs}>
          <Text>{t("follow_us_social_media_text")}</Text>
          <SocialLinks />
        </PaddingWrapper>
        <NotificationBanner breakout />
      </div>
    </PageLayout>
  );
}

export async function getStaticProps({ locale }) {
  const siteSettings = await getSiteSettings();
  const helpEmail = await getHelpEmail();

  const showNewCustomerServiceDetails = await isNewCustomerServiceActive();

  return {
    props: {
      ...(await getAllTranslationsServerSide(locale, ["contact"])),
      siteSettings,
      helpEmail,
      showNewCustomerServiceDetails,
    },
    revalidate: 10,
  };
}
